// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import { EducBox, ChartBox, ResultCard, InputRow, fmt, baseChartOpts, calcIRPEF } from './shared';
import { useDeepLinkParams, hasAllParams, ShareCalcButton, scrollToResult } from './deepLink';

const COEFF = 5.723; // coefficiente trasformazione 2026 a 67 anni

const calcPensione = ({ eta, annoInizio, stipLordo, crescita, tipo, contributiBase }) => {
  const etaNum = Math.max(20, Math.min(66, parseInt(eta) || 35));
  const annoNum = parseInt(annoInizio) || 2015;
  const oggi = new Date().getFullYear();
  const anniLavorati = Math.max(0, oggi - annoNum);
  const anniRimanenti = Math.max(0, 67 - etaNum);
  const annoPensione = etaNum + anniRimanenti + (oggi - etaNum - anniLavorati);
  const g = (parseFloat(crescita) || 0) / 100;
  const stipAnnuo = parseFloat(stipLordo) || 30000;
  const rivalutazione = 0.015;

  const aliquote = { dipendente: 0.33, pubblico: 0.33, autonomo: 0.2623, professionista: 0.16 };
  const aliquota = aliquote[tipo] || 0.33;

  let montante = parseFloat(contributiBase) || 0;

  // Stima contributi già versati (solo se contributiBase == 0)
  if (montante === 0 && anniLavorati > 0) {
    for (let y = 0; y < anniLavorati; y++) {
      const stipPassato = stipAnnuo / Math.pow(1 + g, anniLavorati - y);
      montante = (montante + stipPassato * aliquota) * (1 + rivalutazione);
    }
  }

  const labels = [String(oggi)];
  const series = [Math.round(montante)];

  for (let y = 1; y <= anniRimanenti; y++) {
    const stipProiettato = stipAnnuo * Math.pow(1 + g, y);
    montante = (montante + stipProiettato * aliquota) * (1 + rivalutazione);
    if (y % Math.max(1, Math.floor(anniRimanenti / 12)) === 0 || y === anniRimanenti) {
      labels.push(String(oggi + y));
      series.push(Math.round(montante));
    }
  }

  const pensioneLordaAnnua = montante * (COEFF / 100);
  const pensioneLordaMensile = pensioneLordaAnnua / 13;
  const irpef = calcIRPEF(pensioneLordaAnnua);
  const pensioneNettaMensile = (pensioneLordaAnnua - irpef) / 13;

  const irpefStip = calcIRPEF(stipAnnuo);
  const stipNettoMensile = (stipAnnuo - irpefStip) / 12;
  const tasso = stipNettoMensile > 0 ? (pensioneNettaMensile / stipNettoMensile) * 100 : 0;
  const gap = Math.max(0, stipNettoMensile - pensioneNettaMensile);

  return { montante, pensioneLordaMensile, pensioneNettaMensile, tasso, gap, anniRimanenti, annoPensione: oggi + anniRimanenti, labels, series };
};

const PensioneINPS = () => {
  const url = useDeepLinkParams({ eta: 'int', ral: 'num' });
  const [eta, setEta] = useState(url.eta ?? '35');
  const [annoInizio, setAnnoInizio] = useState('2015');
  const [stipendio, setStipendio] = useState(url.ral ?? '30000');
  const [crescita, setCrescita] = useState('1.5');
  const [tipo, setTipo] = useState('dipendente');
  const [contributi, setContributi] = useState('0');

  useEffect(() => {
    if (hasAllParams(url, ['eta', 'ral'])) scrollToResult('pensione-result');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const r = useMemo(() => calcPensione({ eta, annoInizio, stipLordo: stipendio, crescita, tipo, contributiBase: contributi }), [eta, annoInizio, stipendio, crescita, tipo, contributi]);

  const tassoColor = r.tasso >= 65 ? '#3D7A52' : r.tasso >= 50 ? '#C9A84C' : '#E24B4A';

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <EducBox text={`Per chi lavora dal 1996, la pensione pubblica è stimata al ~55% dell'ultimo stipendio. Questo calcolatore usa il sistema contributivo 2026: il montante accumulato si converte in pensione tramite il coefficiente di trasformazione (${COEFF}% a 67 anni).`} />

      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <InputRow label="Età attuale" value={eta} onChange={setEta} min={20} max={66} />
        <InputRow label="Anno inizio lavoro" value={annoInizio} onChange={setAnnoInizio} min={1980} max={2025} />
        <InputRow label="Stipendio lordo annuo (€)" value={stipendio} onChange={setStipendio} prefix="€" />
        <InputRow label="Crescita stipendio attesa (%/anno)" value={crescita} onChange={setCrescita} suffix="%" tooltip="Media storica italiana per lavoratori dipendenti" />
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="text-[#888780] text-sm block mb-1">Tipo lavoratore</label>
          <select value={tipo} onChange={e => setTipo(e.target.value)} className="w-full bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md px-3 py-2 text-sm" data-testid="tipo-lavoratore-select">
            <option value="dipendente">Dipendente privato (33%)</option>
            <option value="pubblico">Dipendente pubblico (33%)</option>
            <option value="autonomo">Autonomo (26.23%)</option>
            <option value="professionista">Libero professionista (16%)</option>
          </select>
        </div>
        <InputRow label="Contributi INPS versati finora (€)" value={contributi} onChange={setContributi} prefix="€" tooltip="Trovi il montante contributivo su myINPS (sezione 'La mia pensione'). Lascia 0 per stimarlo." />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4" data-testid="pensione-result">
        <div className="col-span-2 sm:col-span-1">
          <ResultCard label="Pensione mensile netta stimata" value={`€${fmt(r.pensioneNettaMensile)}`} gold />
        </div>
        <ResultCard label="Pensione mensile lorda" value={`€${fmt(r.pensioneLordaMensile)}`} />
        <ResultCard label="Tasso di sostituzione" value={`${r.tasso.toFixed(1)}%`} sub={r.tasso >= 65 ? '✓ Buono' : r.tasso >= 50 ? '⚠ Medio' : '⚠ Basso'} green={r.tasso >= 65} />
        <ResultCard label="Gap mensile da colmare" value={r.gap > 0 ? `€${fmt(r.gap)}/mese` : 'Nessun gap'} sub="Stipendio netto – pensione" />
        <ResultCard label="Anni alla pensione" value={`${r.anniRimanenti} anni`} sub={`Anno stimato: ${r.annoPensione}`} gold />
        <ResultCard label="Montante accumulato stimato" value={`€${fmt(r.montante)}`} sub="Al momento della pensione" />
      </div>

      {r.gap > 0 && (
        <div className="p-4 rounded-xl mb-4" style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.3)' }}>
          <p className="text-sm font-medium mb-1" style={{ color: '#C9A84C' }}>Colma il gap pensionistico</p>
          <p className="text-sm text-[#888780]">
            Per colmare il gap di <strong className="text-[#E8E8E4]">€{fmt(r.gap)}/mese</strong>, considera un fondo pensione complementare.{' '}
            <a href="/calcolatori/pensione-complementare" className="underline" style={{ color: '#C9A84C' }}>
              → Calcola con Pensione Complementare
            </a>
          </p>
        </div>
      )}

      <ChartBox title="Proiezione montante contributivo nel tempo (€)">
        <Line
          data={{ labels: r.labels, datasets: [{ label: 'Montante contributivo', data: r.series, borderColor: '#3D7A52', backgroundColor: 'rgba(61,122,82,0.12)', fill: true, tension: 0.4, pointRadius: 0 }] }}
          options={baseChartOpts()}
        />
      </ChartBox>

      <ShareCalcButton slug="pensione-inps" testid="pensione-inps-share-btn"
        getValues={() => ({ eta, ral: stipendio })} />

      <p className="text-xs text-[#888780] mt-4 leading-relaxed" style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 12 }}>
        Stima indicativa basata sul sistema contributivo 2026. I coefficienti di trasformazione vengono aggiornati ogni 2 anni dall'INPS. Per il tuo estratto conto reale visita <strong>myINPS.inps.it</strong> — sezione "La mia pensione futura".
      </p>
</motion.div>
  );
};

export default PensioneINPS;
